import React from 'react';

/**
 * Friendly empty state used across venue/staff/admin surfaces (Slice C).
 *
 * Renders a circular icon halo + title + subtitle + optional CTA.
 * Kept intentionally minimal so it composes inside cards and lists.
 *
 * Props:
 *   icon       — Lucide icon component (e.g. LineChart, MessageSquare, UserPlus)
 *   iconColor  — Stroke color for the icon (default Allure purple)
 *   title      — Big heading line
 *   description — Subtitle paragraph
 *   actionLabel — Optional CTA pill text
 *   onAction    — Optional CTA click handler
 *   testId      — data-testid on the wrapper
 *   compact     — Smaller vertical rhythm (use inside cards/lists)
 */
export default function VenueEmptyState({
  icon: Icon,
  iconColor = 'var(--accent-purple)',
  title,
  description,
  actionLabel,
  onAction,
  testId,
  compact = false,
}) {
  return (
    <div
      data-testid={testId}
      style={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        padding: compact ? '20px 16px' : '32px 20px',
        gap: 10,
      }}
    >
      {Icon ? (
        <div
          aria-hidden="true"
          style={{
            width: compact ? 64 : 80,
            height: compact ? 64 : 80,
            borderRadius: 999,
            background: 'var(--accent-surface-soft)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 6,
          }}
        >
          <Icon size={compact ? 30 : 36} color={iconColor} strokeWidth={1.6} />
        </div>
      ) : null}
      {title ? (
        <p
          style={{
            margin: 0,
            fontFamily: "'Inria Serif', serif",
            fontSize: compact ? 17 : 19,
            fontWeight: 700,
            color: 'var(--text)',
            lineHeight: 1.2,
            maxWidth: 280,
          }}
        >
          {title}
        </p>
      ) : null}
      {description ? (
        <p
          style={{
            margin: 0,
            fontFamily: "'Inria Serif', serif",
            fontSize: compact ? 13 : 14,
            color: 'var(--text-3)',
            lineHeight: 1.4,
            maxWidth: 300,
          }}
        >
          {description}
        </p>
      ) : null}
      {actionLabel && onAction ? (
        <button
          type="button"
          onClick={onAction}
          data-testid={testId ? `${testId}-action` : undefined}
          className="premium-pill-button"
          style={{
            marginTop: 10,
            padding: '10px 22px',
            fontFamily: "'Inria Serif', serif",
            fontSize: 15,
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          {actionLabel}
        </button>
      ) : null}
    </div>
  );
}
